import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Divider, Stack, InputAdornment, IconButton,
  Alert, CircularProgress, Backdrop, Link,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Microsoft as MicrosoftIcon, MailOutline, LockOutlined, Visibility, VisibilityOff,
  NotificationsNoneOutlined, InsightsOutlined, AccountTreeOutlined,
} from '@mui/icons-material';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { loginUnificado, loginGoogleUnificado } from '../componentes/http/actions/loginUnificadoAction';
import { externalLoginUser } from '../componentes/http/actions/authAction';
import { msalInstance } from '../componentes/security/msal-config';
import Image from '../assets/Alerta Normativa-logo2026-claro.png';
import ImageHero from '../assets/FondoNewsletter.png';
import ImageBlanco from '../assets/Alerta Normativa-logo2026-blanco.png';
import { useColorMode } from '../styles/ColorModeProvider';
import { brand } from '../styles/theme';
import ThemeToggle from '../componentes/navegacion/ThemeToggle';

// URL del formulario de suscripción (para Google no registrado)
const URL_FORMULARIO = 'https://alertanormativa.com/suscripcion-normativa/';

const HERO_RGB = '12, 52, 32';
const HERO_BG = `
  linear-gradient(90deg, rgba(${HERO_RGB}, 0.97) 0%, rgba(${HERO_RGB}, 0.88) 42%, rgba(${HERO_RGB}, 0.55) 100%),
  linear-gradient(180deg, rgba(${HERO_RGB}, 0.35) 0%, rgba(${HERO_RGB}, 0) 35%, rgba(${HERO_RGB}, 0.8) 100%),
  url("${ImageHero}") center / cover no-repeat #0e3b2c
`;

const TITULAR = ['La normativa que impacta ', 'a tu organización,', 'en un solo lugar.'];

const DESTACADOS = [
  { icon: <NotificationsNoneOutlined />, texto: 'Alertas oportunas de nuevas normativas y cambios regulatorios' },
  { icon: <InsightsOutlined />, texto: 'Evaluación de impacto y seguimiento de compromisos' },
  { icon: <AccountTreeOutlined />, texto: 'Integración con AustranetGRC' },
];

const alertSx = { width: '100%', mb: 2, animation: 'anFadeUp 0.3s ease-in-out' };

// Lee el rol del JWT para decidir a dónde redirigir tras login.
const rolDeToken = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]))["rol"] || null;
  } catch {
    return null;
  }
};

const LoginForm = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode } = useColorMode();
  const isDark = mode === 'dark';

  const accent = theme.palette.primary.main;

  const socialBtn = isDark
    ? { bg: '#131314', border: '#8e918f', text: '#e3e3e3', hover: '#1f1f21' }
    : { bg: '#ffffff', border: '#dadce0', text: '#3c4043', hover: '#f7f9fc' };

  const [loginData, setLoginData] = useState({ Email: '', Password: '' });
  const [loginError, setLoginError] = useState('');
  const [noRegistrado, setNoRegistrado] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeLoginMethod, setActiveLoginMethod] = useState(null);
  const [isUILocked, setIsUILocked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // El botón de Google requiere un ancho en px: se ajusta al contenedor (máx. 400)
  const socialRef = useRef(null);
  const [googleWidth, setGoogleWidth] = useState(400);
  useEffect(() => {
    const el = socialRef.current;
    if (!el) return;
    const update = () => setGoogleWidth(Math.min(400, Math.floor(el.clientWidth)));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let t;
    if (loginError) t = setTimeout(() => setLoginError(''), 6000);
    return () => t && clearTimeout(t);
  }, [loginError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
  };

  const entrarConToken = (token) => {
    const rol = rolDeToken(token);
    const destino = rol === 'Suscriptor' ? '/suscriptor/bandeja' : '/home';
    navigate(destino, { replace: true });
  };

  // ── Login email + password (cascada interno → suscriptor) ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading || isUILocked) return;

    setLoginError('');
    setNoRegistrado(false);
    setIsLoading(true);
    setActiveLoginMethod('standard');

    try {
      const res = await loginUnificado(loginData.Email.trim(), loginData.Password);
      if (res.ok && res.token) {
        entrarConToken(res.token);
      } else {
        setLoginError(res.message || 'Correo o contraseña incorrectos.');
        setIsLoading(false);
        setActiveLoginMethod(null);
      }
    } catch (error) {
      console.error('Error en login:', error);
      setLoginError('No se pudo conectar con el servidor.');
      setIsLoading(false);
      setActiveLoginMethod(null);
    }
  };

  // ── Login Google (cascada interno → suscriptor) ──
  const handleGoogleSuccess = async (credentialResponse) => {
    setIsUILocked(false);
    if (isLoading) return;
    if (!credentialResponse?.credential) {
      setLoginError('Error al iniciar sesión con Google. Inténtelo de nuevo.');
      return;
    }

    setIsLoading(true);
    setLoginError('');
    setNoRegistrado(false);
    setActiveLoginMethod('google');

    try {
      const res = await loginGoogleUnificado(credentialResponse.credential);
      if (res.ok && res.token) {
        entrarConToken(res.token);
      } else if (res.noRegistrado) {
        setNoRegistrado(true);
        setIsLoading(false);
        setActiveLoginMethod(null);
      } else {
        setLoginError(res.message || 'No se pudo iniciar sesión con Google.');
        setIsLoading(false);
        setActiveLoginMethod(null);
      }
    } catch (error) {
      console.error('Error en login Google:', error);
      setLoginError('No se pudo conectar con el servidor.');
      setIsLoading(false);
      setActiveLoginMethod(null);
    }
  };

  const handleGoogleError = () => {
    setIsUILocked(false);
    setLoginError('Error al iniciar sesión con Google. Inténtelo de nuevo.');
    setActiveLoginMethod(null);
    setIsLoading(false);
  };

  // ── Login Microsoft (solo internos) ──
  const handleMicrosoftLogin = async () => {
    if (isLoading || isUILocked) return;

    setIsUILocked(true);
    setIsLoading(true);
    setLoginError('');
    setActiveLoginMethod('microsoft');

    try {
      const loginRequest = { scopes: ['user.read', 'profile', 'email', 'openid'] };
      await msalInstance.initialize();
      const response = await msalInstance.loginPopup(loginRequest);

      if (response && response.idToken) {
        // externalLoginUser guarda la sesión; aquí solo se necesita el token para el rol.
        let capturado = null;
        const result = await externalLoginUser(
          'Microsoft',
          response.idToken,
          setLoginError,
          (tok) => { capturado = tok; }
        );
        if (result && capturado) {
          entrarConToken(capturado);
        } else {
          setIsLoading(false);
          setActiveLoginMethod(null);
        }
      } else {
        setLoginError('No se pudo obtener el token de autenticación');
        setIsLoading(false);
        setActiveLoginMethod(null);
      }
    } catch (error) {
      console.error('Error Microsoft:', error);
      if (error?.errorCode !== 'user_cancelled') {
        setLoginError('No se pudo iniciar sesión con Microsoft. Inténtelo de nuevo.');
      }
      setIsLoading(false);
      setActiveLoginMethod(null);
    } finally {
      setIsUILocked(false);
    }
  };

  const disabled = isLoading || isUILocked;

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '6px',
      bgcolor: 'background.paper',
      transition: 'box-shadow .2s ease',
      '& fieldset': { borderColor: alpha(theme.palette.text.primary, isDark ? 0.24 : 0.16) },
      '&:hover fieldset': { borderColor: alpha(theme.palette.text.primary, 0.4) },
      '&.Mui-focused fieldset': { borderColor: accent, borderWidth: '1.5px' },
      '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(accent, isDark ? 0.25 : 0.1)}` },
    },
    '& .MuiOutlinedInput-input': { py: '11.5px', fontSize: '0.95rem' },
    '& .MuiOutlinedInput-input::placeholder': { color: theme.palette.text.secondary, opacity: 0.9 },
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
      WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.paper} inset`,
      WebkitTextFillColor: theme.palette.text.primary,
      caretColor: theme.palette.text.primary,
      transition: 'background-color 9999s ease-out 0s',
    },
  };

  return (
    <>
      <Backdrop
        sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1, backgroundColor: 'rgba(0,0,0,0.2)' }}
        open={isUILocked}
        onClick={() => setIsUILocked(false)}
      />

      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          bgcolor: 'background.default',
        }}
      >
        {/* ── Panel de marca (escritorio) ── */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 6,
            color: '#fff',
            background: HERO_BG,
            px: { md: 6, lg: 8 },
            py: { md: 5, lg: 6 },
          }}
        >
          <Box
            component="img"
            src={ImageBlanco}
            alt="Alerta Normativa Global"
            sx={{ height: 100, width: 'auto', display: 'block', alignSelf: 'flex-start', ml: 6, mt: 2 }}
          />

          <Box sx={{ maxWidth: 560, ml: 6}}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontSize: 'clamp(1.75rem, 2.4vw, 2.6rem)',
                fontWeight: 600,
                lineHeight: 1.16,
                letterSpacing: '-0.02em',
                textWrap: 'balance',
              }}
            >
              {TITULAR.map((linea) => (
                <Box key={linea} component="span" sx={{ display: 'block' }}>
                  {linea}
                </Box>
              ))}
            </Typography>
            <Typography
              sx={{
                mt: 2.5, maxWidth: 460, fontSize: '1.02rem', lineHeight: 1.6,
                color: 'rgba(255,255,255,0.82)', textWrap: 'pretty',
              }}
            >
              Monitorea, evalúa y gestiona el cumplimiento regulatorio con información oportuna y trazable.
            </Typography>

            <Stack spacing={2} sx={{ mt: 5 }}>
              {DESTACADOS.map((d) => (
                <Box key={d.texto} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40, height: 40, borderRadius: '8px', flexShrink: 0,
                      display: 'grid', placeItems: 'center',
                      bgcolor: alpha(brand.teal, 0.24),
                      border: '1px solid rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(4px)',
                      '& svg': { fontSize: 20 },
                    }}
                  >
                    {d.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.95rem', lineHeight: 1.45, maxWidth: 360,
                      color: 'rgba(255,255,255,0.92)', textWrap: 'pretty',
                    }}
                  >
                    {d.texto}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', ml: 6 }}>
            © {new Date().getFullYear()} Austranet · Alerta Normativa Global
          </Typography>
        </Box>

        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2.5, sm: 4 },
            py: { xs: 6, sm: 8 },
            backgroundImage: `radial-gradient(ellipse 70% 50% at 100% 0%, ${alpha(brand.teal, isDark ? 0.12 : 0.09)}, transparent 70%)`,
          }}
        >
          <ThemeToggle
            sx={{
              position: 'absolute', top: 20, right: 20,
              width: 40, height: 40,
              borderRadius: '10px',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              color: 'text.primary',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          />

          <Box sx={{ width: '100%', maxWidth: 400, animation: 'anFadeUp .45s ease both' }}>
            <Box
              component="img"
              src={isDark ? ImageBlanco : Image}
              alt="Alerta Normativa"
              sx={{ height: 72, display: { xs: 'block', md: 'none' }, mx: 'auto', mb: 4 }}
            />

            <Typography
              variant="h4"
              component="h2"
              align="center"
              sx={{ fontSize: { xs: '1.6rem', sm: '1.9rem' }, fontWeight: 600, letterSpacing: '-0.01em' }}
            >
              Iniciar sesión
            </Typography>
            <Typography align="center" color="text.secondary" sx={{ mt: 0.75, mb: 4, fontSize: '0.95rem' }}>
              Ingresa con tu cuenta para continuar.
            </Typography>

            {loginError && (
              <Alert severity="error" sx={alertSx} onClose={() => setLoginError('')}>
                {loginError}
              </Alert>
            )}

            {noRegistrado && (
              <Alert severity="warning" sx={alertSx}>
                No estás registrado con este correo.
                <Box sx={{ mt: 1 }}>
                  <Link href={URL_FORMULARIO} sx={{ fontWeight: 600 }} target="_self" rel="noopener">
                    Regístrate aquí
                  </Link>
                </Box>
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={1.75}>
                <TextField
                  required fullWidth id="email" name="Email" type="email"
                  placeholder="Correo electrónico"
                  autoComplete="email" autoFocus
                  value={loginData.Email} onChange={handleChange}
                  disabled={disabled}
                  sx={fieldSx}
                  inputProps={{ 'aria-label': 'Correo electrónico' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutline fontSize="small" sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  required fullWidth id="password" name="Password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Contraseña"
                  autoComplete="current-password"
                  value={loginData.Password} onChange={handleChange}
                  disabled={disabled}
                  sx={fieldSx}
                  inputProps={{ 'aria-label': 'Contraseña' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                          onClick={() => setShowPassword((v) => !v)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          size="small"
                          sx={{ color: 'text.secondary' }}
                        >
                          {showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Button
                type="submit" fullWidth variant="contained" color="primary"
                disabled={disabled}
                sx={{
                  mt: 3, height: 46, borderRadius: '6px', fontSize: '0.95rem',
                  '&.Mui-disabled': { bgcolor: alpha(accent, 0.65), color: '#fff' },
                }}
              >
                {activeLoginMethod === 'standard' && isLoading ? <CircularProgress size={22} color="inherit" /> : 'Iniciar sesión'}
              </Button>
            </Box>

            <Divider sx={{ my: 3.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem', px: 1 }}>
                o continúa con
              </Typography>
            </Divider>

            <Box ref={socialRef} sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <Button
                fullWidth variant="outlined"
                onClick={handleMicrosoftLogin} disabled={disabled}
                startIcon={
                  activeLoginMethod === 'microsoft' && isLoading
                    ? <CircularProgress size={18} color="inherit" />
                    : <MicrosoftIcon />
                }
                sx={{
                  height: 40, borderRadius: '4px',
                  textTransform: 'none', fontSize: '0.875rem', fontWeight: 500, letterSpacing: '0.01em',
                  bgcolor: socialBtn.bg, color: socialBtn.text, borderColor: socialBtn.border,
                  '& .MuiButton-startIcon': { mr: 1.25 },
                  '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 18 },
                  '&:hover': { bgcolor: socialBtn.hover, borderColor: socialBtn.border },
                  '&.Mui-disabled': { bgcolor: socialBtn.bg, color: socialBtn.text, borderColor: socialBtn.border, opacity: 0.6 },
                }}
              >
                Iniciar sesión con Microsoft
              </Button>

              <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  size="large" width={googleWidth}
                  logo_alignment="center" shape="rectangular"
                  theme={isDark ? 'filled_black' : 'outline'}
                />
              </GoogleOAuthProvider>
            </Box>

            {isLoading && !activeLoginMethod && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, width: '100%' }}>
                <CircularProgress size={28} color="primary" />
              </Box>
            )}

            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              sx={{ display: { xs: 'block', md: 'none' }, mt: 5 }}
            >
              © {new Date().getFullYear()} Austranet · Alerta Normativa Global
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default LoginForm;